/* eslint-disable no-undef */
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import { UNInput, UNModal } from "un-react-ui-components";
import { useIntl } from "react-intl";
import { RoutePath } from "../../defines/route-path";
import { notify } from "../../libs/observables/notification";
import { RequestCreateData } from "../../core/models/Request";
import { useRequestsContext } from "../../contexts/requests/RequestsContext";
import { useRequestsActions } from "../../contexts/requests/UseRequestsActions";

type RequestModalProps = {
  open: boolean;
  onClose(): void;
  id?: string;
};

const NewRequestModal = ({ open, onClose, id }: RequestModalProps) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState<string>();
  const [errorTitle, setErrorTitle] = useState<string>("");
  const [legalText, setLegalText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const { selectedRequest, requestsError } = useRequestsContext();
  const { dispatchCreateRequest, dispatchUpdateRequest, dispatchGetRequests } =
    useRequestsActions();

  const intl = useIntl();
  const copyTextID = intl.formatMessage(
    {
      id: "copyClipboard",
    },
    { text: id }
  );

  //#region effects
  useEffect(() => {
    if (!id) {
      if (selectedRequest && !requestsError) {
        notify("success")({ message: "Request created!" });
        goToSteps();
      }
    } else {
      if (selectedRequest) {
        dispatchGetRequests({
          page: 1,
          size: 1000,
        });
        if (loading && selectedRequest && !requestsError) {
          notify("success")({ message: "Request updated!" });
          onCancel();
        } else {
          setTitle(selectedRequest.title);
          setLegalText(selectedRequest.legalText);
        }
      }
    }

    setLoading(false);
  }, [selectedRequest, requestsError]);
  //#endregion

  //#region Modal Actions
  const onSave = () => {
    if (!title) {
      setErrorTitle("Request title is required!");
      return;
    }

    setLoading(true);

    const request: RequestCreateData = {
      title,
      legal_text: legalText,
      id,
    };
    if (id) {
      dispatchUpdateRequest(request);
    } else {
      dispatchCreateRequest(request);
    }
  };
  const onCancel = () => {
    setTitle("");
    setLegalText("");
    onClose();
  };
  const goToSteps = () => {
    if (selectedRequest) {
      navigate(RoutePath.STEPS.replace(":id", selectedRequest.id));
    }
  };
  //#region end

  //#region Renderers
  const content = (
    <Box className="space-y-12">
      <Grid
        data-testid="newRequestModal.filters"
        container
        alignItems="center"
        direction={"row"}
      >
        <Grid size={{ xs: 12 }} className="space-y-[1.875rem]">
          <UNInput
            id="request.title"
            data-testid="request.title"
            labelTranslationKey="request.new.title.label"
            value={title}
            onChange={setTitle}
            error={errorTitle}
          />
          {id && (
            <UNInput
              id="request.id"
              labelTranslationKey="request.new.idAbbr.label"
              value={id}
              onChange={undefined}
              disabled
              copy
              copyText={copyTextID}
              copyTextTranslationKey={undefined}
            />
          )}
          {/**<UNInput
            id="request.url"
            labelTranslationKey="request.new.url.label"
            value={url}
            onChange={undefined}
            disabled
            copy
            copyText={copyTextUrl}
            copyTextTranslationKey={undefined}
          />**/}
          <UNInput
            labelTranslationKey={"request.new.legal.info.label"}
            placeholderTranslationKey={"request.new.legalInfo.placeholder"}
            multiline
            value={legalText}
            onChange={setLegalText}
            readOnly={false}
            className={"mb-4 pb-8"}
          />
          {/**
          <Box
            flex={1}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems={"center"}
            className={"mb-4 pb-8"}
          >
            <UNSwitch
              label={undefined}
              labelTranslationKey="request.new.public.label"
              labelTranslationKeyParam={undefined}
              value={isPublicChallenge}
              onChange={setIsPublicChallenge}
            />
            <Button
              data-testid="request.testQr"
              variant="outlined"
              onClick={() => {}}
            >
              <IntlMessages id="request.new.testQr.button" />
            </Button>
          </Box>**/}
        </Grid>
      </Grid>
    </Box>
  );

  const actions = (
    <>
      <Typography>
        <span></span>
      </Typography>
      <Box className="flex space-x-[1.375rem]">
        <Button
          data-testid="newRequestModal.cancel"
          variant="text"
          color="error"
          onClick={onCancel}
        >
          <IntlMessages id="cancel" />
        </Button>
        <Button data-testid="newRequestModal.save" onClick={onSave}>
          <IntlMessages id="save" />
        </Button>
      </Box>
    </>
  );
  //#endregion

  return (
    <UNModal
      id="newRequestModal"
      titleTranslationKey="request.new.title"
      showTitleBorder={false}
      open={open}
      loading={false}
      onClose={() => onClose()}
      actions={actions}
      maxWidth="md"
    >
      <Backdrop open={loading}>
        <CircularProgress data-testid="loader" />
      </Backdrop>
      {content}
    </UNModal>
  );
};

export { NewRequestModal };
